import { getAuthHeaders } from './api';
import { Activity } from '../types/Activity';
import { auth } from '../config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ============================================================================
// AI API INTERFACES
// ============================================================================

export interface AISearchRequest {
  query: string;
  location?: {
    city?: string;
    country?: string;
  };
  filters?: {
    category_id?: number;
    skill_level?: string;
    max_participants?: number;
    date_from?: string;
    date_to?: string;
  };
}

export interface AIAnalysis {
  intent: string;
  confidence_score: number;
  categories: string[];
  query_type: 'specific_activity' | 'general_interest' | 'location_based' | 'skill_based';
  extracted_filters?: {
    skill_level?: string;
    categories?: string[];
    location_preferences?: string[];
  };
}

export interface AISearchResponse {
  success: boolean;
  data: {
    activities: Activity[];
    ai_analysis: AIAnalysis;
    conversation_response: string;
    follow_up_questions: string[];
    search_id: number; // For tracking interactions
    total_results: number;
  };
  error?: string;
}

export interface StartConversationRequest {
  initial_query: string;
  context?: {
    location?: {
      city?: string;
      country?: string;
    };
    user_preferences?: Record<string, unknown>;
  };
}

export interface StartConversationResponse {
  success: boolean;
  data: {
    session_id: string;
    welcome_message: string;
    conversation_id: number;
    suggested_queries?: string[];
  };
  error?: string;
}

export interface ContinueConversationRequest {
  session_id: string;
  message: string;
  context?: {
    location?: {
      city?: string;
      country?: string;
    };
    selected_activities?: number[];
    user_feedback?: string;
  };
}

export interface ContinueConversationResponse {
  success: boolean;
  data: {
    message: string;
    activities?: Activity[];
    follow_up_questions?: string[];
    conversation_state: 'greeting' | 'searching' | 'refining' | 'recommending' | 'completed';
    search_id?: number;
  };
  error?: string;
}

export interface ConversationSummaryResponse {
  success: boolean;
  data: {
    session_id: string;
    conversation_history: Array<{
      role: 'user' | 'assistant';
      message: string;
      timestamp: string;
      activities?: Activity[];
    }>;
    user_preferences_learned: Record<string, unknown>;
    recommended_activities: Activity[];
    conversation_insights: {
      main_interests: string[];
      preferred_locations: string[];
      skill_levels: string[];
      activity_types: string[];
    };
  };
  error?: string;
}

export interface TrackInteractionRequest {
  search_id: number;
  action: 'activity_clicked' | 'activity_bookmarked' | 'activity_registered' | 'search_refined' | 'conversation_continued';
  activity_id?: number;
  additional_data?: Record<string, unknown>;
}

export interface TrackInteractionResponse {
  success: boolean;
  message: string;
}

export interface AIRecommendationsRequest {
  type: 'personalized' | 'similar_activities' | 'location_based' | 'trending' | 'interest_based';
  limit?: number;
  activity_id?: number; // For similar_activities type
  location?: {
    city?: string;
    country?: string;
  };
}

export interface AIRecommendationsResponse {
  success: boolean;
  data: {
    recommendations: Activity[];
    reasoning: string;
    confidence_score: number;
    recommendation_type: string;
    total_available: number;
  };
  error?: string;
}

export interface AIFeedbackRequest {
  feedback_type: 'search_quality' | 'recommendations' | 'conversation' | 'general';
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string;
  search_id?: number;
  conversation_id?: number;
  recommendation_id?: number;
}

export interface AIFeedbackResponse {
  success: boolean;
  message: string;
  feedback_id: number;
}

export interface AIAnalyticsResponse {
  success: boolean;
  data: {
    total_searches: number;
    total_conversations: number;
    total_recommendations_generated: number;
    average_search_satisfaction: number;
    popular_search_intents: Array<{
      intent: string;
      count: number;
      success_rate: number;
    }>;
    user_engagement_metrics: {
      searches_per_session: number;
      conversation_length_avg: number;
      recommendation_click_rate: number;
    };
    period: {
      start_date: string;
      end_date: string;
      days: number;
    };
  };
  error?: string;
}

// ============================================================================
// AI API FUNCTIONS
// ============================================================================

/**
 * Perform AI-powered natural language search
 */
export const aiSearch = async (searchRequest: AISearchRequest): Promise<AISearchResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/search/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchRequest),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `AI search failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('AI search error:', error);
    throw error;
  }
};

/**
 * Start a new AI conversation session
 */
export const startAIConversation = async (request: StartConversationRequest): Promise<StartConversationResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/conversation/start/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to start AI conversation: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Start AI conversation error:', error);
    throw error;
  }
};

/**
 * Continue an existing AI conversation
 */
export const continueAIConversation = async (request: ContinueConversationRequest): Promise<ContinueConversationResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/conversation/continue/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to continue AI conversation: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Continue AI conversation error:', error);
    throw error;
  }
};

/**
 * Get conversation summary and history
 */
export const getConversationSummary = async (sessionId: string): Promise<ConversationSummaryResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/conversation/summary/?session_id=${encodeURIComponent(sessionId)}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get conversation summary: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Get conversation summary error:', error);
    throw error;
  }
};

/**
 * Track user interactions for AI learning
 */
export const trackAIInteraction = async (request: TrackInteractionRequest): Promise<TrackInteractionResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/track-interaction/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to track AI interaction: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Track AI interaction error:', error);
    throw error;
  }
};

/**
 * Get AI-powered activity recommendations
 */
export const getAIRecommendations = async (request: AIRecommendationsRequest): Promise<AIRecommendationsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams({
      type: request.type,
      limit: (request.limit || 10).toString(),
    });

    if (request.activity_id) {
      queryParams.append('activity_id', request.activity_id.toString());
    }

    if (request.location?.city) {
      queryParams.append('city', request.location.city);
    }

    if (request.location?.country) {
      queryParams.append('country', request.location.country);
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/recommendations/?${queryParams.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get AI recommendations: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Get AI recommendations error:', error);
    throw error;
  }
};

/**
 * Submit feedback about AI features
 */
export const submitAIFeedback = async (request: AIFeedbackRequest): Promise<AIFeedbackResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/ai/feedback/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to submit AI feedback: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Submit AI feedback error:', error);
    throw error;
  }
};

/**
 * Get AI analytics and usage statistics
 */
export const getAIAnalytics = async (days: number = 30, scope: 'personal' | 'admin' = 'personal'): Promise<AIAnalyticsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams({
      days: days.toString(),
      scope,
    });

    const response = await fetch(`${API_BASE_URL}/api/ai/analytics/?${queryParams.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get AI analytics: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Get AI analytics error:', error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is authenticated (required for AI features)
 */
export const isAIFeatureAvailable = (): boolean => {
  // AI features are only available to authenticated users
  // Check if Firebase user is currently authenticated
  try {
    return !!auth.currentUser;
  } catch {
    // Fallback: if Firebase is not available, return false
    return false;
  }
};

/**
 * Format AI confidence score for display
 */
export const formatConfidenceScore = (score: number): string => {
  if (score >= 0.9) return 'Very High';
  if (score >= 0.7) return 'High';
  if (score >= 0.5) return 'Medium';
  if (score >= 0.3) return 'Low';
  return 'Very Low';
};

/**
 * Get AI search suggestions based on user context
 */
export const getAISearchSuggestions = (userLocation?: { city?: string; country?: string }): string[] => {
  const baseSuggestions = [
    "I want to learn something new this weekend",
    "Looking for outdoor activities for beginners",
    "Help me find cooking classes near me",
    "I'm interested in fitness but don't know where to start",
    "Find me creative workshops for adults",
    "I want to meet people with similar interests",
  ];

  if (userLocation?.city) {
    return [
      `Find fun activities in ${userLocation.city}`,
      `What's popular in ${userLocation.city} this week?`,
      `Beginner-friendly events in ${userLocation.city}`,
      ...baseSuggestions,
    ];
  }

  return baseSuggestions;
};

/**
 * Extract key insights from AI analysis for display
 */
export const extractAIInsights = (analysis: AIAnalysis): string[] => {
  const insights: string[] = [];

  if (analysis.confidence_score >= 0.8) {
    insights.push(`High confidence match (${Math.round(analysis.confidence_score * 100)}%)`);
  }

  if (analysis.categories.length > 0) {
    insights.push(`Categories: ${analysis.categories.join(', ')}`);
  }

  if (analysis.extracted_filters?.skill_level) {
    insights.push(`Skill level: ${analysis.extracted_filters.skill_level}`);
  }

  if (analysis.query_type) {
    const typeLabels = {
      specific_activity: 'Specific Activity Request',
      general_interest: 'General Interest',
      location_based: 'Location-Based Search',
      skill_based: 'Skill-Based Search',
    };
    insights.push(`Search type: ${typeLabels[analysis.query_type]}`);
  }

  return insights;
};

